/**
 * StatusTransitionModal — confirms either direction of the collection
 * lifecycle (draft → active, or active → draft). The page picks which
 * mutation to call via `onConfirm`; this modal owns the copy + the
 * confirm UI only.
 */

import React from 'react';

import { Button } from '../../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentStatus: 'active' | 'draft';
  onConfirm: () => void;
  isPending: boolean;
}

const StatusTransitionModal: React.FC<Props> = ({
  open,
  onOpenChange,
  currentStatus,
  onConfirm,
  isPending,
}) => {
  const activating = currentStatus === 'draft';
  const title = activating
    ? 'Activate this collection?'
    : 'Deactivate this collection?';
  const description = activating
    ? 'Activating makes the Datasets, Split, Overview, Coverage, and Quality tabs visible to annotators. You can deactivate later.'
    : 'Deactivating hides the Datasets, Split, Overview, Coverage, and Quality tabs. Data is not deleted. You can reactivate later.';
  const confirmLabel = activating ? 'Activate' : 'Deactivate';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="status-transition-confirm"
          >
            {isPending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StatusTransitionModal;
